import { useEffect, useRef, useCallback, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'

interface WebSSHModalProps {
  tenantID: string
  deviceID: string
  deviceName: string
  onClose: () => void
}

// Binary WebSocket protocol:
//   Client → Server:
//     [0x00][data...]                              – stdin data
//     [0x01][cols_hi][cols_lo][rows_hi][rows_lo]   – terminal resize (5 bytes)
//   Server → Client:
//     raw bytes = merged stdout+stderr

function buildResizeMsg(cols: number, rows: number): Uint8Array {
  const buf = new Uint8Array(5)
  buf[0] = 0x01
  const v = new DataView(buf.buffer)
  v.setUint16(1, cols, false) // big-endian
  v.setUint16(3, rows, false)
  return buf
}

function buildDataMsg(data: string): Uint8Array {
  const encoded = new TextEncoder().encode(data)
  const buf = new Uint8Array(1 + encoded.length)
  buf[0] = 0x00
  buf.set(encoded, 1)
  return buf
}

export default function WebSSHModal({ tenantID, deviceID, deviceName, onClose }: WebSSHModalProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const termRef = useRef<Terminal | null>(null)
  const fitRef = useRef<FitAddon | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const auth = useAuth()
  const [connState, setConnState] = useState<'connecting' | 'connected' | 'closed' | 'error'>('connecting')
  const [reconnecting, setReconnecting] = useState(false)

  const connect = useCallback(() => {
    const term = termRef.current!
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const base = `${protocol}://${window.location.host}`
    const token = auth.user?.access_token ?? ''
    const url = `${base}/api/v1/tenants/${tenantID}/devices/${deviceID}/ssh?token=${encodeURIComponent(token)}`

    setConnState('connecting')
    term.writeln('\x1b[90mConnecting to \x1b[0m' + deviceName + '\x1b[90m...\x1b[0m')

    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.onopen = () => {
      setConnState('connected')
      setReconnecting(false)
      term.write('\x1b[2K\r') // clear the "Connecting..." line
      // Defer the initial fit+resize to the next animation frame so the modal
      // container has its final dimensions before we calculate cols/rows.
      // Without this, fit() runs before CSS layout is complete and reports a
      // default 80x24/24x80 geometry — vi then renders with wrong terminal size.
      requestAnimationFrame(() => {
        const fit = fitRef.current!
        fit.fit()
        ws.send(buildResizeMsg(term.cols, term.rows))
      })
    }

    ws.onmessage = (e: MessageEvent) => {
      if (e.data instanceof ArrayBuffer) {
        term.write(new Uint8Array(e.data))
      } else if (typeof e.data === 'string') {
        term.write(e.data)
      }
    }

    ws.onclose = (e: CloseEvent) => {
      setConnState('closed')
      setReconnecting(false)
      term.writeln('\r\n\x1b[90m[Connection closed]\x1b[0m')
      // Normal closure (code 1000) means the remote shell exited cleanly (user typed "exit").
      // Auto-dismiss the modal after a short delay so the user sees the message.
      if (e.code === 1000) {
        setTimeout(() => onClose(), 1500)
      }
    }

    ws.onerror = () => {
      setConnState('error')
      setReconnecting(false)
      term.writeln('\r\n\x1b[31m[Connection error]\x1b[0m')
    }
  }, [tenantID, deviceID, deviceName])

  useEffect(() => {
    if (!containerRef.current) return

    const term = new Terminal({
      theme: {
        background: '#0f172a',
        foreground: '#e2e8f0',
        cursor: '#38bdf8',
        selectionBackground: '#38bdf840',
        black: '#1e293b',
        brightBlack: '#475569',
        red: '#f87171',
        brightRed: '#ef4444',
        green: '#4ade80',
        brightGreen: '#22c55e',
        yellow: '#fbbf24',
        brightYellow: '#f59e0b',
        blue: '#60a5fa',
        brightBlue: '#3b82f6',
        magenta: '#c084fc',
        brightMagenta: '#a855f7',
        cyan: '#38bdf8',
        brightCyan: '#0ea5e9',
        white: '#cbd5e1',
        brightWhite: '#f1f5f9',
      },
      fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", "Consolas", monospace',
      fontSize: 13,
      lineHeight: 1.4,
      cursorBlink: true,
      cursorStyle: 'bar',
      scrollback: 5000,
      allowTransparency: true,
    })
    const fit = new FitAddon()
    term.loadAddon(fit)
    term.open(containerRef.current)
    fit.fit()
    term.focus()

    termRef.current = term
    fitRef.current = fit

    term.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(buildDataMsg(data))
      }
    })

    term.onResize(({ cols, rows }) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(buildResizeMsg(cols, rows))
      }
    })

    connect()

    const handleResize = () => fit.fit()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      wsRef.current?.close()
      term.dispose()
    }
  }, [connect])

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        display: 'flex', flexDirection: 'column',
        width: 900, maxWidth: '95vw', height: 600, maxHeight: '90vh',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,229,182,0.06)',
      }}>
        {/* Title bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 12px 0 16px', height: 44,
          background: 'var(--color-surface-2)',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
          userSelect: 'none',
        }}>
          {/* Left: status dot + label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
              flexShrink: 0, width: 8, height: 8, borderRadius: '50%',
              background: connState === 'connected'  ? 'var(--color-accent)'   :
                          connState === 'connecting' ? 'var(--color-amber)'    :
                          connState === 'error'      ? 'var(--color-danger)'   : 'var(--color-text-dim)',
              boxShadow: connState === 'connected'  ? '0 0 6px var(--color-accent-glow)' :
                         connState === 'error'      ? '0 0 6px rgba(255,69,96,0.5)'      : 'none',
              animation: connState === 'connecting' ? 'pulse 1.2s ease-in-out infinite' : 'none',
            }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-muted)' }}>
              SSH
            </span>
            <span style={{
              fontSize: 13, fontWeight: 500, color: 'var(--color-text-bright)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {deviceName}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-dim)', flexShrink: 0 }}>
              {deviceID.slice(0, 8)}
            </span>
          </div>

          {/* Right: reconnect + close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 16 }}>
            {/* Reconnect */}
            <button
              onClick={() => {
                wsRef.current?.close()
                setReconnecting(true)
                connect()
              }}
              disabled={reconnecting || connState === 'connecting'}
              title="Reconnect"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', height: 28,
                background: 'var(--color-accent-dim)',
                border: '1px solid rgba(0,229,182,0.25)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-accent)',
                fontSize: 12, fontWeight: 500, fontFamily: 'var(--font)',
                cursor: reconnecting || connState === 'connecting' ? 'not-allowed' : 'pointer',
                opacity: reconnecting || connState === 'connecting' ? 0.45 : 1,
                transition: 'var(--transition)',
              }}
              onMouseEnter={e => {
                if (!reconnecting && connState !== 'connecting') {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,182,0.18)'
                  ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,229,182,0.5)'
                }
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-accent-dim)'
                ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,229,182,0.25)'
              }}
            >
              <svg
                style={{ width: 13, height: 13, animation: reconnecting ? 'spin 0.8s linear infinite' : 'none' }}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.3}
              >
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reconnect
            </button>

            {/* Close */}
            <button
              onClick={onClose}
              title="Close terminal"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28,
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
                transition: 'var(--transition)',
              }}
              onMouseEnter={e => {
                const b = e.currentTarget as HTMLButtonElement
                b.style.background = 'var(--color-danger-dim)'
                b.style.borderColor = 'rgba(255,69,96,0.3)'
                b.style.color = 'var(--color-danger)'
              }}
              onMouseLeave={e => {
                const b = e.currentTarget as HTMLButtonElement
                b.style.background = 'transparent'
                b.style.borderColor = 'transparent'
                b.style.color = 'var(--color-text-muted)'
              }}
            >
              <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Terminal container */}
        <div style={{ flex: 1, overflow: 'hidden', padding: 8 }}>
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        </div>
      </div>
    </div>
  )
}
